import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';

/** Instance data from the Vertex API */
interface InstanceData {
  instanceId: string;
  name: string;
  status: string;
  uptimeSeconds: number;
  loadedPlugins: string[];
  configVersion: number;
  lastSeenAt: string;
  deploymentType: string;
  managed: boolean;
}

/** VTAK Instance Management page */
export default function InstanceManagement(): React.JSX.Element {
  useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [instances, setInstances] = useState<InstanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const loadInstances = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/vertex/instances');
      if (res.ok) setInstances(await res.json() as InstanceData[]);
    } catch { /* API unavailable */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadInstances(); }, [loadInstances]);

  const handleCreate = async (data: CreateFormData) => {
    try {
      const res = await fetch('/api/vertex/instances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setShowCreate(false);
        await loadInstances();
      }
    } catch { /* error */ }
  };

  const handleDownload = async (instanceId: string) => {
    try {
      const res = await fetch(`/api/vertex/instances/${instanceId}/config-bundle`);
      if (!res.ok) return;
      const bundle = await res.json() as { name: string; envFile: string; dockerCompose: string };
      downloadTextFile(`${bundle.name}.env`, bundle.envFile);
      downloadTextFile(`${bundle.name}-docker-compose.yml`, bundle.dockerCompose);
    } catch { /* error */ }
  };

  const handleDecommission = async (instanceId: string) => {
    await fetch(`/api/vertex/instances/${instanceId}`, { method: 'DELETE' });
    await loadInstances();
  };

  const blueBtnStyle: React.CSSProperties = {
    height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
    textTransform: 'uppercase', cursor: 'pointer',
  };

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          VTAK Instances
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowCreate(!showCreate)} style={blueBtnStyle}>
            {showCreate ? 'Cancel' : 'New Instance'}
          </button>
          <button onClick={loadInstances} style={{ ...blueBtnStyle, background: 'transparent', border: `1px solid ${colors.border}`, color: colors.text }}>
            Refresh
          </button>
        </div>
      </div>

      {showCreate && <CreateForm onSubmit={handleCreate} isDark={isDark} colors={colors} />}

      {loading ? (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading instances...</p>
      ) : instances.length === 0 ? (
        <EmptyState colors={colors} />
      ) : (
        <InstanceTable instances={instances} isDark={isDark} colors={colors}
          onDownload={handleDownload} onDecommission={handleDecommission} />
      )}
    </div>
  );
}

// ─── Create Form ───

interface CreateFormData {
  name: string;
  role: string;
  plugins: string;
  port: number;
  deploymentType: string;
}

function CreateForm({ onSubmit, isDark, colors }: {
  onSubmit: (data: CreateFormData) => void; isDark: boolean; colors: ColorTheme;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [role, setRole] = useState('tactical');
  const [plugins, setPlugins] = useState('space,force-protect');
  const [port, setPort] = useState(3000);
  const [deploymentType, setDeploymentType] = useState('local-dev');

  const inputStyle: React.CSSProperties = {
    height: '36px', padding: '0 12px', borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
    color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: FONTS.heading, fontSize: '9px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: colors.textSecondary, marginBottom: '4px',
  };

  return (
    <div style={{ padding: '16px', borderRadius: '10px', border: `1px solid ${colors.border}`, marginBottom: '16px', backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card }}>
      <div className="vx-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        <div><div style={labelStyle}>Instance Name</div><input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Vandenberg SFS Dev" style={{ ...inputStyle, width: '100%' }} /></div>
        <div><div style={labelStyle}>Role</div><select value={role} onChange={e => setRole(e.target.value)} style={{ ...inputStyle, width: '100%', cursor: 'pointer' }}>
          <option value="tactical">Tactical</option>
          <option value="operational">Operational</option>
          <option value="strategic">Strategic</option>
        </select></div>
        <div><div style={labelStyle}>Deployment</div><select value={deploymentType} onChange={e => setDeploymentType(e.target.value)} style={{ ...inputStyle, width: '100%', cursor: 'pointer' }}>
          <option value="local-dev">Local Dev</option>
          <option value="remote">Remote Docker</option>
          <option value="airgapped">Air-Gapped</option>
        </select></div>
        <div><div style={labelStyle}>Plugins (comma-separated)</div><input value={plugins} onChange={e => setPlugins(e.target.value)} style={{ ...inputStyle, width: '100%' }} /></div>
        <div><div style={labelStyle}>Port</div><input type="number" value={port} onChange={e => setPort(parseInt(e.target.value) || 3000)} style={{ ...inputStyle, width: '100%' }} /></div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button onClick={() => onSubmit({ name, role, plugins, port, deploymentType })} disabled={!name.trim()} style={{
            height: '36px', width: '100%', borderRadius: '6px', border: 'none',
            background: 'linear-gradient(90deg, #01A9ED, #0165C6)',
            color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
            textTransform: 'uppercase', cursor: name.trim() ? 'pointer' : 'not-allowed',
            opacity: name.trim() ? 1 : 0.5,
          }}>Create</button>
        </div>
      </div>
    </div>
  );
}

// ─── Instance Table ───

function InstanceTable({ instances, isDark, colors, onDownload, onDecommission }: {
  instances: InstanceData[]; isDark: boolean; colors: ColorTheme;
  onDownload: (id: string) => void; onDecommission: (id: string) => void;
}): React.JSX.Element {
  const headerStyle: React.CSSProperties = {
    padding: '10px 16px', textAlign: 'left', fontFamily: FONTS.heading,
    fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase',
    color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`,
  };
  const cellStyle: React.CSSProperties = {
    padding: '12px 16px', fontFamily: FONTS.body, fontSize: '13px',
    color: colors.text, borderBottom: `1px solid ${colors.border}`,
  };

  return (
    <div className="vx-table-wrap" style={{ borderRadius: '10px', border: `1px solid ${colors.border}`, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt }}>
            <th style={headerStyle}>Name</th>
            <th style={headerStyle}>Status</th>
            <th style={headerStyle}>Plugins</th>
            <th style={headerStyle}>Uptime</th>
            <th style={headerStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {instances.map(inst => (
            <tr key={inst.instanceId}>
              <td style={{ ...cellStyle, fontWeight: 600 }}>
                {inst.name}
                <div style={{ fontSize: '10px', color: colors.textSecondary, fontWeight: 400 }}>{inst.instanceId}</div>
              </td>
              <td style={cellStyle}><StatusBadge status={inst.status} /></td>
              <td style={cellStyle}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {inst.loadedPlugins.map(m => <PluginBadge key={m} name={m} isDark={isDark} />)}
                </div>
              </td>
              <td style={{ ...cellStyle, color: colors.textSecondary }}>{formatUptime(inst.uptimeSeconds)}</td>
              <td style={cellStyle}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {inst.managed && <ActionBtn label="Download Config" onClick={() => onDownload(inst.instanceId)} isDark={isDark} colors={colors} />}
                  {inst.status !== 'decommissioned' && <ActionBtn label="Decommission" onClick={() => onDecommission(inst.instanceId)} isDark={isDark} colors={colors} danger />}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Small Components ───

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const COLORS_MAP = new Map([
    ['healthy', { bg: 'rgba(74,222,128,0.15)', text: '#4ADE80', dot: '#4ADE80' }],
    ['pending', { bg: 'rgba(250,204,21,0.15)', text: '#FACC15', dot: '#FACC15' }],
    ['degraded', { bg: 'rgba(250,204,21,0.15)', text: '#FACC15', dot: '#FACC15' }],
    ['unreachable', { bg: 'rgba(239,68,68,0.15)', text: '#EF4444', dot: '#EF4444' }],
    ['decommissioned', { bg: 'rgba(160,160,160,0.15)', text: '#999', dot: '#999' }],
  ]);
  const c = COLORS_MAP.get(status) ?? { bg: 'rgba(160,160,160,0.15)', text: '#999', dot: '#999' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px', borderRadius: '12px', backgroundColor: c.bg, fontFamily: FONTS.body, fontSize: '11px', fontWeight: 600, color: c.text }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: c.dot }} />{status}
    </span>
  );
}

function PluginBadge({ name, isDark }: { name: string; isDark: boolean }): React.JSX.Element {
  return <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', fontFamily: FONTS.body, color: isDark ? '#AAA' : '#555' }}>{name}</span>;
}

function ActionBtn({ label, onClick, colors, danger }: {
  label: string; onClick: () => void; isDark?: boolean; colors: ColorTheme; danger?: boolean;
}): React.JSX.Element {
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', borderRadius: '4px', fontSize: '10px', border: `1px solid ${danger ? '#EF4444' : colors.border}`,
      backgroundColor: 'transparent', color: danger ? '#EF4444' : colors.text,
      fontFamily: FONTS.body, cursor: 'pointer',
    }}>{label}</button>
  );
}

function EmptyState({ colors }: { colors: ColorTheme }): React.JSX.Element {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <p style={{ fontFamily: FONTS.heading, fontSize: '14px', color: colors.text, marginBottom: '8px' }}>No instances registered</p>
      <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>Create an instance or start a VTAK server with VERTEX_API_URL configured</p>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
